// webapp/src/components/ErrorBoundaryFallback.tsx
//
// Phase 31.6 Plan 02 — FE-31-07 / D-01. Phase 97: copy moved to the i18n
// catalogue (`errorBoundary.*`) — this screen is mounted by main.tsx BELOW
// every page, so a French-only fallback was the last thing an English
// visitor saw when everything else had failed. It sits INSIDE
// LanguageProvider (main.tsx nesting), so useLanguage() is safe here.
// Branded fallback for the root <Sentry.ErrorBoundary>. Renders on ANY
// uncaught render error below the boundary (React componentDidCatch lifecycle,
// independent of whether Sentry.init ran).
//
// THE TRAP (RESEARCH Pitfall 2): @sentry/react@10.52.0 ALWAYS returns a non-null
// uuid4 `eventId` in the fallback render-prop — even under the kill-switch
// (VITE_SENTRY_ENABLED=false → init never ran → no client). So the support Réf.
// line MUST be gated on `Sentry.getClient()` truthiness, NOT `eventId`.
//
// SECURITY (RESEARCH §V14 / T-31.6-03): NEVER render `error.message` /
// `componentStack` / stack — generic catalogue copy + eventId-derived ref only.
//
// Gemini Gate 1 finding #3: use optional chaining `eventId?.slice(...)`
// defensively — a throw inside the fallback would be an uncatchable white screen.
//
// Refs: phase 31.6

import * as Sentry from '@sentry/react'
import { useLanguage } from '../i18n/LanguageProvider'

// v10 fallback render-prop shape (verified against @sentry/react@10.52.0).
interface ErrorBoundaryFallbackProps {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}

// Open the Sentry User Feedback form so the user can describe what happened.
// The report is linked to the current error event (associated_event_id). No-op
// when the feedback integration is not loaded (kill-switch / no DSN).
async function openFeedback(): Promise<void> {
  const feedback = Sentry.getFeedback()
  if (!feedback) return
  try {
    const form = await feedback.createForm()
    form.appendToDom()
    form.open()
  } catch {
    /* feedback widget unavailable — silently skip (never white-screen the fallback) */
  }
}

export default function ErrorBoundaryFallback(_props: ErrorBoundaryFallbackProps) {
  // Gate the Réf. line on the Sentry client, NOT on eventId (RESEARCH Pitfall 2):
  // a client only exists when initSentry() actually called Sentry.init.
  const { t } = useLanguage()
  const sentryActive = !!Sentry.getClient()
  const eventId = _props.eventId
  const ref = sentryActive && eventId ? `ERR-${eventId?.slice(0, 5).toUpperCase()}` : null

  return (
    <main className="pt-28 pb-20 px-6 min-h-screen flex items-center justify-center">
      <div className="max-w-[420px] w-full">
        <div className="bg-surface border border-border rounded-[8px] p-6 md:p-8 text-center">
          <img src="/logo.svg" alt="SweatStudio" className="h-10 mx-auto mb-6" />
          <h1 className="font-display font-[800] text-[clamp(1.8rem,4vw,2.5rem)] leading-tight tracking-[-0.02em] mb-4">
            {t('errorBoundary.title')}
          </h1>
          <p className="text-text-muted text-base mb-6">
            {t('errorBoundary.body')}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 hover:-translate-y-px cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg w-full"
          >
            {t('errorBoundary.reload')}
          </button>
          {sentryActive && (
            <button
              type="button"
              onClick={openFeedback}
              className="mt-3 border border-border hover:border-accent text-text-muted hover:text-text font-medium px-6 py-3 rounded-[4px] transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg w-full"
            >
              {t('errorBoundary.report')}
            </button>
          )}
          {ref && (
            <p className="font-mono text-[10px] text-text-muted/70 select-all mt-4">
              {t('errorBoundary.ref', { ref })}
            </p>
          )}
        </div>
      </div>
    </main>
  )
}
